import { z } from 'zod'
import axios from 'axios'
import { useSuspenseQuery } from '@tanstack/react-query'
import { TeamLogoSchema } from '../assets/api'

async function getRaces(params: RaceQueryParams) {
  const response = await axios.get(
    `/api/competitions/${params.competition_id}/races`,
    { params }
  )
  return z.array(RaceSchema).parse(response.data)
}

export function useRaces(params: RaceQueryParams) {
  return useSuspenseQuery({
    queryKey: ['races', params],
    queryFn: () => getRaces(params),
  })
}

async function getRaceDetails({
  competition_id,
  race_id,
}: {
  competition_id: number
  race_id: number
}) {
  const response = await axios.get(
    `/api/competitions/${competition_id}/races/${race_id}`
  )
  return RaceSchema.parse(response.data)
}

export function useRaceDetails({
  competition_id,
  race_id,
}: {
  competition_id: number
  race_id: number
}) {
  return useSuspenseQuery({
    queryKey: ['raceDetails', { competition_id, race_id }],
    queryFn: () => getRaceDetails({ competition_id, race_id }),
  })
}

async function getRaceStatsDetails({
  competition_id,
  race_id,
  division,
}: {
  competition_id: number
  race_id: number
  division?: number
}) {
  const queryParams: Record<string, number> = {}

  if (division !== undefined) {
    queryParams.division = division
  }

  const response = await axios.get(
    `/api/competitions/${competition_id}/races/${race_id}/leaderboard`,
    { params: queryParams }
  )
  return RaceStatsSchema.parse(response.data)
}

export function useRaceStatsDetails({
  competition_id,
  race_id,
  division,
}: {
  competition_id: number
  race_id: number
  division?: number
}) {
  return useSuspenseQuery({
    queryKey: ['raceStatsDetails', { competition_id, race_id, division }],
    queryFn: () => getRaceStatsDetails({ competition_id, race_id, division }),
  })
}

async function getTeamRaceStats(params: TeamRaceStatsQueryParams) {
  const response = await axios.get(
    `/api/competitions/${params.competition_id}/teams/${params.team_id}/race-stats`,
    { params }
  )
  return TeamRaceStatsSchema.parse(response.data)
}

export function useTeamRaceStats(params: TeamRaceStatsQueryParams) {
  return useSuspenseQuery({
    queryKey: ['teamRaceStats', params],
    queryFn: () => getTeamRaceStats(params),
  })
}

const ResultSchema = z.object({
  team_id: z.number().int().positive(),
  team_name: z.string(),
  team_logo: TeamLogoSchema,
  race_result_id: z.number().int().positive().optional(),
  score: z.number().nonnegative().optional(),
})

const RaceSchema = z.object({
  race_id: z.number().int().positive(),
  round_number: z.number().int().positive(),
  round_start: z.string().date(),
  round_status: z.enum(['ended', 'live', 'upcoming']),
  division_id: z.number().int().positive().nullable(),
  video: z
    .object({
      main: z.string().url().optional(),
    })
    .passthrough()
    .catch({ main: undefined }),
  results: z.array(ResultSchema).optional(),
})

const KpiSchema = z.object({
  metric_id: z.number().int().positive(),
  metric_key: z.string(),
  metric_name: z.string(),
  percent: z.number().nullable(),
})

const StatsResultSchema = ResultSchema.extend({
  rank: z.number().int().positive(),
  rank_change: z.number().int(),
  race_points: z.number(),
  race_rating: z.number(),
  form: z.array(z.string()),
  kpis: z.array(KpiSchema),
})

const RaceStatsSchema = RaceSchema.omit({ round_status: true }).extend({
  status: z.enum(['ended', 'live', 'upcoming']),
  race_name: z.string(),
  round_end: z.string().date(),
  results: z.array(StatsResultSchema),
})

const SeasonStatsSchema = z.object({
  starts: z.number().int().nonnegative(),
  wins: z.number().int().nonnegative(),
  top_3: z.number().int().nonnegative(),
  top_5: z.number().int().nonnegative(),
  avg_race_score: z.number(),
})

const RecentRaceSchema = z.object({
  race_id: z.number().int().positive(),
  round_number: z.number().int().positive(),
  round_start: z.string().date(),
  round_end: z.string().date(),
  result_rank: z.number().int().positive(),
  race_points: z.number().nonnegative(),
  week: z.string(),
})

const TeamRaceStatsSchema = z.object({
  team_id: z.number().int().positive(),
  team_name: z.string(),
  season_stats: SeasonStatsSchema,
  recent_races: z.array(RecentRaceSchema),
})

type TeamRaceStatsQueryParams = {
  competition_id: number
  team_id: number
  round_start?: number
  round_end?: number
}

type RaceQueryParams = {
  competition_id: number
  division?: number
  round_start?: number
  round_end?: number
}

export type Race = z.infer<typeof RaceSchema>
export type StatsResult = z.infer<typeof StatsResultSchema>
export type SeasonStats = z.infer<typeof SeasonStatsSchema>
export type RecentRace = z.infer<typeof RecentRaceSchema>
